import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Save as SaveIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { TeamsApi, Team } from "api-client";

interface TeamDetailsSettingsProps {
  team: Team;
  apiClient: TeamsApi;
  onTeamUpdate: (team: Team) => void;
  onTeamDelete: () => void;
}

const TeamDetailsSettings: React.FC<TeamDetailsSettingsProps> = ({
  team,
  apiClient,
  onTeamUpdate,
  onTeamDelete,
}) => {
  const [name, setName] = useState(team.name);
  const [slug, setSlug] = useState(team.slug || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError("Team name is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updatedTeam = await apiClient.teamsUpdate({
        id: team.id,
        team: {
          name: name.trim(),
          slug: slug.trim() || undefined,
        } as Omit<Team, "id" | "created_at" | "updated_at">,
      });

      onTeamUpdate(updatedTeam);
    } catch {
      // Removed console.error for lint compliance
      setError("Failed to update team details");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiClient.teamsDestroy({ id: team.id });
      onTeamDelete();
    } catch {
      setError("Failed to delete team");
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const generateSlug = (teamName: string) => {
    return teamName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleNameChange = (value: string) => {
    setName(value);
    // Auto-generate slug if slug is empty or matches the current auto-generated pattern
    const currentAutoSlug = generateSlug(team.name);
    if (!slug || slug === currentAutoSlug) {
      setSlug(generateSlug(value));
    }
  };

  const canEdit = team.isAdmin;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, overflowWrap: 'anywhere' }}>
      <Typography variant="h6" gutterBottom>
        Team Information
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSave}>
        <Stack spacing={3}>
          <TextField
            label="Team Name"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
            fullWidth
            disabled={!canEdit || loading}
            helperText="A descriptive name for your team"
          />

          <TextField
            label="Team ID (Slug)"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            fullWidth
            disabled={!canEdit || loading}
            helperText="Unique identifier used in URLs (no spaces allowed)"
            inputProps={{ pattern: "[a-z0-9-]+" }}
          />

          <TextField
            label="Team ID"
            value={team.id}
            disabled
            fullWidth
            helperText="Internal team ID (cannot be changed)"
          />

          {canEdit && (
            <Stack direction="row" spacing={2} justifyContent="flex-start">
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !name.trim()}
                startIcon={
                  loading ? <CircularProgress size={16} /> : <SaveIcon />
                }
              >
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </Stack>
          )}

          {!canEdit && (
            <Alert severity="info">
              You need administrator permissions to modify team settings.
            </Alert>
          )}
        </Stack>
      </Box>

      {canEdit && (
        <>
          <Divider sx={{ my: 4 }} />

          <Box>
            <Typography variant="h6" gutterBottom color="error">
              Danger Zone
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Permanently delete this team and all associated data. This action
              cannot be undone.
            </Typography>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Team
            </Button>
          </Box>

          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Delete Team?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to permanently delete{" "}
                <strong>{team.name}</strong>?
                <br />
                <br />
                This will delete:
                <ul>
                  <li>All team data and settings</li>
                  <li>All team members and invitations</li>
                  <li>All associated subscriptions and billing</li>
                </ul>
                <strong>This action cannot be undone.</strong>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                color="error"
                variant="contained"
                disabled={deleting}
                startIcon={
                  deleting ? <CircularProgress size={16} /> : <DeleteIcon />
                }
              >
                {deleting ? "Deleting..." : "I understand, delete this team"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default TeamDetailsSettings;
